import type { KpiStripData, TimeseriesData, UnattributedBooks } from '../../api/types.js';
import { color, font, fontSize, radius, seriesColor } from '../../theme/tokens.js';
import { BreakdownPanel, breakdownRows, type BreakdownRow } from '../Breakdown/Breakdown.js';
import { provenanceFor, type MetricProvenance } from '../Breakdown/provenance.js';
import { DataQualityNote } from '../DataQualityBadge/DataQualityBadge.js';
import { Disclosure } from '../Disclosure/Disclosure.js';
import {
  EM_DASH,
  formatValue,
  formatValueExact,
  isCompacted,
} from '../MetricCell/MetricCell.js';

/**
 * The six-card KPI strip (FR-028, FR-029, PRD §6.2, §9.7).
 *
 * **Six, because Candidates was removed** (owner decision 2026-09-02, FR-028). It printed
 * `kpi.candidates`, which FR-029a asserts is the *same figure* as BoB — `KPI BoB = KPI
 * Candidates = the totals row's BOB`, an identity the ingest fails the run over — so the strip
 * showed 20,286 twice, once bare and once with the Nursing/Allied split and its composition
 * bar beside it. The card with the split is strictly the more informative of the two.
 *
 * ⚠ **The metric is still computed and still served.** `kpi.candidates` remains on the wire and
 * `assertKpiIdentities` still checks it against BoB — that check is what would catch the two
 * book passes drifting apart, and it is worth more on the ingest than as a second tile. So this
 * is a card removed, not a figure retired: restoring it is one `<Card>`, and nothing upstream
 * needs to move.
 *
 * **Chart filters never reach these numbers — with one deliberate exception since 2026-09-02.**
 * For five of the six cards it is structural rather than remembered: `/api/regions/overview` does
 * not accept `source`, `targeting` or `specialty` at all, and the figures live in their own
 * `kpi_summary` document with no filter dimension. **Submissions is the exception** (owner
 * decision): it is summed from the chart's own payload, so it follows the grain, the three chart
 * filters and the region pills together, and it says on its face which window it is counting. It
 * needs no filtered endpoint precisely because it never asks the server anything — see
 * `submissionsOverWindow`.
 *
 * **No card moves with the TABLE period, Submissions included.** That control belongs to the
 * panel below and governs the region table; this strip has never answered to it and still does
 * not. Jobs counts open jobs across the whole of `cynet_nexus_jobs` — no period window, no
 * region, no client requirement, no `DISTINCT job_id`. If changing the *period* moves anything on
 * this strip, FR-029 is broken; if changing the *grain* moves anything but Submissions, this
 * amendment has been over-applied.
 *
 * ## Six of the seven cards count the UI regions; Jobs does not (FR-029a, 2026-08-28)
 *
 * Every card built from classified rows now requires the row's recruiter to fold to one of the
 * server's region codes — a bound applied during the ingest, so it is the server's set and not
 * this client's six (`UI_REGIONS`). **Active and Submissions did not before**, which is why
 * Active read ~800 against the ACTV totals row's 481 in the same panel — most of that gap being placements owned
 * by recruiters the alignment table has never heard of.
 *
 * Jobs is exempt because a job has no recruiter to fold. It therefore does NOT agree with the
 * `#JOBS` column in the region table below it, and is not meant to: that column counts distinct
 * jobs *posted in the period* for one region, which is a different question about a different
 * set of rows.
 *
 * ⚠ **The restriction is not annotated on the cards** (owner decision, 2026-08-28). An earlier
 * revision put the excluded count under Active and Submissions; it was removed the same day. The
 * bound is a rule the business chose, so a note would fire on every snapshot forever — and an
 * indicator that never distinguishes anything is the one people learn to dismiss, which is
 * exactly the reasoning behind PRD-Delivery §9.4's deferred book-health flags. `excludedRows`
 * stays on the ingest document as the record of what was set aside; nothing serves or renders it.
 *
 * BoB keeps `<DataQualityNote>`, which is a different thing: it reports the FR-059 **alarm** — a
 * recruiter id reaching no master row — and is structurally silent today (G-25). It used to sit
 * on Candidates too; one card carries it now because one card reports the book.
 */

export interface KpiStripProps {
  kpi: KpiStripData | null;
  unattributedBooks: UnattributedBooks | null | undefined;
  /**
   * The chart's own payload, and the **only** thing on this strip that answers to a control
   * (owner decision 2026-09-02). Submissions is summed from it; every other card ignores it.
   * `null` — no snapshot, or the grain still in flight — leaves the card on the all-time figure.
   */
  timeseries?: TimeseriesData | null;
  loading?: boolean;
}

/** What the Submissions card shows when it is reading the chart. */
export interface SubmissionsWindow {
  total: number | null;
  nursing: number | null;
  allied: number | null;
  internal: number | null;
  external: number | null;
  /**
   * Names the window on the card FACE, abbreviated — `last 12M` (2026-09-10). The label shares
   * its row with `SUBMISSIONS` inside a ~194px tile, so the spelt-out noun wrapped the row onto
   * a second line and pushed the headline down: the one card on the strip that moves was also
   * the one card a third of a line taller than its five neighbours. `M`/`W`/`Q`/`D` is the
   * product's own period vocabulary (the control's `30d`, `24M`), not a squeezed spelling —
   * `MNTHS` saves the same glyphs and has to be decoded.
   */
  label: string;
  /**
   * The same window spelt out — `last 12 months`. Only the face is width-bound, so the
   * accessible name and the disclosure panel keep the words: `last 12M` heard aloud is worse
   * than read, and the panel is where a reader goes for the unabbreviated answer (the same
   * divergence `#JOBS` makes between `164(176)` and its spoken `164 (176)`).
   */
  spokenLabel: string;
}

/** Σ of one series' `subs`, `null` when the whole series is null — never 0 (FR-055). */
function sumSubs(points: ReadonlyArray<{ subs: number | null }> | undefined): number | null {
  const known = (points ?? []).map((p) => p.subs).filter((v): v is number => v != null);
  return known.length === 0 ? null : known.reduce((n, v) => n + v, 0);
}

const WINDOW_NOUN: Record<TimeseriesData['grain'], string> = {
  daily: 'days',
  weekly: 'weeks',
  monthly: 'months',
  quarterly: 'quarters',
};

/**
 * The face's unit, one letter per grain — `last 12M`, `last 12W`, `last 12Q`, `last 12D`.
 *
 * ⚠ **`last` stays.** It is what says the figure is a trailing sum rather than the newest
 * bucket, and dropping it to save four glyphs leaves `SUBMISSIONS · 12M` beside a chart whose
 * x-axis is twelve monthly buckets — a label that reads as the grain, not as the window.
 */
const WINDOW_UNIT: Record<TimeseriesData['grain'], string> = {
  daily: 'D',
  weekly: 'W',
  monthly: 'M',
  quarterly: 'Q',
};

/**
 * Submissions over the window the chart is drawing (owner decision 2026-09-02, amending FR-029).
 *
 * ⚠ **This is the one card on the strip that moves**, and it moves with everything the chart
 * does — the grain, Source, Targeting, Specialty and the region pills — because it is summed
 * from the very payload the line is drawn from. That is the point: the card is the total of the
 * line above it, so the two cannot disagree. It costs no request; `<RegionsOverview>` already
 * holds this response.
 *
 * **The label is not decoration.** The other six cards answer to nothing, so a seventh that
 * silently changed when someone touched a chart control is exactly the confusion FR-034 is
 * about. `last 12M` on the face is what distinguishes "this figure moved" from "this figure is
 * broken", and it rides in the card's own label so the accessible name carries it too — spelt
 * out there (`spokenLabel`), because only the face has 194px to fit in.
 *
 * **`external` is `total − internal`**, derived here rather than fetched — the same rule
 * `api/regions.ts` states for the hover breakdown, so the two can never disagree. `nursing +
 * allied = total` exactly (FR-071 makes every submission clinical); measured on the live
 * snapshot at all three grains before this card was allowed to sum them, because the
 * composition bar under it draws parts of one whole and nothing else may go in it.
 */
export function submissionsOverWindow(
  timeseries: TimeseriesData | null | undefined,
): SubmissionsWindow | null {
  if (!timeseries || timeseries.points.length === 0) return null;

  const total = sumSubs(timeseries.points);
  const internal = sumSubs(timeseries.breakdown?.internal);

  return {
    total,
    nursing: sumSubs(timeseries.breakdown?.nursing),
    allied: sumSubs(timeseries.breakdown?.allied),
    internal,
    external: total === null || internal === null ? null : total - internal,
    label: `last ${timeseries.points.length}${WINDOW_UNIT[timeseries.grain]}`,
    spokenLabel: `last ${timeseries.points.length} ${WINDOW_NOUN[timeseries.grain]}`,
  };
}

/**
 * The figures behind a card, as the rows the label's disclosure reveals (owner request
 * 2026-09-08).
 *
 * Every card prints its parts in 11px subtext and compacts anything at or above 1,000, so the
 * face says `14.15K Nursing · 6.22K Allied` and the exact figures live nowhere a reader can
 * reach. These rows are where they live: `<BreakdownPanel>`, the same panel the region table and
 * the chart decompose a number with, so a KPI tile and the table under it answer a hover the
 * same way — one title, `Total`, then the parts, every digit kept (`formatValueExact`).
 *
 * **`breakdownRows` builds them wherever the axes are the standard ones**, rather than a second
 * list of labels and hues here. That is what keeps `External = total − internal` one derivation
 * — the rule `api/regions.ts` states and this card already follows — and Internal/Nursing teal
 * against External/Allied indigo one palette. Only the three cards with axes of their own
 * (Clusters, Recruiters, and Jobs' target halves) name their rows here.
 *
 * ⚠ **Nothing on this strip may gain a row that is not a part of the figure above it.** Jobs'
 * `Target` is the exception that proves it: it is the same jobs counted a second way and it
 * appears here because the card face already prints it, not as a slice — `Total` is Nursing +
 * Allied and no arithmetic in this panel says otherwise. A trend row would be a worse version of
 * the same mistake, since the strip answers to no window (FR-029).
 */
function neutralRow(label: string, value: number | null | undefined): BreakdownRow {
  // The bubble's own row ink, not `color.ink`: these rows have no axis to colour them by, and a
  // page-ink figure on the amber panel reads as a different kind of value from the ones beside it.
  return { label, value: formatValueExact(value ?? null), color: color.tipInkRow };
}

function targetRow(label: string, value: number | null | undefined): BreakdownRow {
  return { label, value: formatValueExact(value ?? null), color: color.target };
}

/**
 * The panel, built once and revealed from **both** triggers on a tile — the label row and the
 * headline figure.
 *
 * The first cut gave the figure a panel of its own carrying only that number's digits, and it
 * read as a tooltip that had lost the split: the number is the thing a reader points at, so the
 * bubble it opens has to be the whole decomposition, not the half of it the figure knows about.
 * One function so the two can never drift into two answers for one tile.
 */
function cardPanel(
  title: string,
  total: number | null | undefined,
  rows: BreakdownRow[],
  provenance: MetricProvenance | undefined,
) {
  return (
    <BreakdownPanel
      title={title}
      total={formatValueExact(total)}
      rows={rows}
      provenance={provenance}
    />
  );
}

/**
 * A card's headline figure, compacted with the exact value one hover or one `Tab` away.
 *
 * This strip carries the largest numbers in the product — Submissions all-time is 308,696 and
 * BoB is 20,135 — so it is where compaction earns the most and where losing the digits would
 * cost the most. `<Disclosure>` is what stops it being a loss: the same component the tables use,
 * so the exact figure reaches hover, keyboard focus and assistive technology on equal terms
 * (FR-062) rather than through a pointer-only `title`.
 *
 * A figure short enough to print in full gets no disclosure at all — a control that reveals the
 * number already on screen is one the reader learns to ignore.
 */
function Figure({
  label,
  metric,
  figures,
  provenance,
  color: valueColor,
}: {
  label: string;
  metric: number | null | undefined;
  /** The parts of this figure — the same rows the label row reveals. */
  figures: BreakdownRow[];
  /** How the figure is counted and where it came from — the same footer the tables carry. */
  provenance?: MetricProvenance;
  color?: string;
}) {
  const shown = formatValue(metric);
  const style: React.CSSProperties = {
    fontFamily: font.mono,
    fontSize: fontSize.kpiValue,
    fontWeight: 600,
    color: valueColor ?? color.ink,
  };

  /*
   * A figure short enough to print in full AND with no parts to show gets no disclosure — a
   * control that reveals the number already on screen is one the reader learns to ignore. Active
   * is the only card that lands there.
   *
   * ⚠ The `figures.length` half is what changed on 2026-09-08: `isCompacted` alone left the big
   * number on Clusters and Recruiters inert while their splits sat one row above, and it is the
   * number a reader points at.
   */
  /*
   * ⚠ Since 2026-09-10 the lineage counts as something to reveal, so `provenance` joins the
   * test: Active prints in full and has no parts, but "as-of, from Run Rate" is exactly the
   * thing a reader hovers that card to learn. Without it the one card on the strip with nothing
   * else to say would also be the one that says nothing.
   */
  if (!isCompacted(metric) && figures.length === 0 && !provenance) {
    return (
      <div className="mono" style={style}>
        {shown}
      </div>
    );
  }

  return (
    <Disclosure
      // `Label: 20,365` — unchanged, and deliberately not the label row's name: two triggers on
      // one tile, told apart by ear as well as by eye, revealing the same panel.
      accessibleName={`${label}: ${formatValueExact(metric)}`}
      panel={cardPanel(label, metric, figures, provenance)}
      style={{ padding: 0, background: 'none', border: 'none', cursor: 'default' }}
    >
      <span className="mono" style={style}>
        {shown}
      </span>
    </Disclosure>
  );
}

function Card({
  label,
  metricKey,
  qualifier,
  qualifierSpoken,
  figures,
  value,
  subtext,
  valueColor,
  children,
}: {
  label: string;
  /**
   * The served metric this tile is a reading of, for the panel's lineage footer.
   *
   * ⚠ It is the **metric's** key, not the card's label: `Active` is `activeToa` and
   * `Submissions` is `subs`, the same entries the region table's `ACTV` and `#SUBS` columns
   * look up. Two names for one figure is how a strip and the table under it come to describe
   * the same number differently, which is the drift `provenanceFor`'s key-not-label rule exists
   * to prevent.
   */
  metricKey: string;
  /**
   * The window the figure covers, printed beside the label in a lighter ink — only Submissions
   * has one. It is folded into the figure's accessible name rather than kept visual-only, so a
   * screen-reader user is told which window moved as well as that the number did.
   */
  qualifier?: string;
  /**
   * The spelt-out form of `qualifier`, for the accessible names and the disclosure panel title.
   * The face is abbreviated because it shares a 194px row with the label; nothing else here is
   * width-bound, so nothing else abbreviates. Defaults to `qualifier` when they are the same
   * string — the all-time fallback needs no short form.
   */
  qualifierSpoken?: string;
  /**
   * The parts of the figure, revealed from the label row. The headline itself is the panel's
   * `Total`, so a card whose figure has no parts — Active — passes none.
   */
  figures: BreakdownRow[];
  /** The raw figure, so the card can compact it and still hand back every digit on hover. */
  value: number | null | undefined;
  subtext?: React.ReactNode;
  valueColor?: string;
  children?: React.ReactNode;
}) {
  const spoken = qualifierSpoken ?? qualifier;
  const provenance = provenanceFor(metricKey);

  return (
    <div
      style={{
        // Tiles on a panel, not cards on the page: the strip is rendered inside the Overview's
        // chart panel, where a white card on a white surface would read as a doubled frame.
        background: color.surfaceSubtle,
        border: `1px solid ${color.borderSubtle}`,
        borderRadius: radius['4xl'],
        padding: '14px 16px',
        // A column, so anything a card appends can be pushed to its foot. Grid items already
        // stretch to the tallest tile in the row; without this the composition bars sat wherever
        // their own subtext ended, and BoB's wraps to two lines while Jobs' does not — three bars
        // on three different baselines, which reads as a mistake rather than a set.
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/*
       * The label row reveals the card's figures — the headline exact, then its parts (owner
       * request 2026-09-08). The whole row is the target: `width: 100%` on the trigger, so a
       * three-letter label like BOB is as easy to hit as SUBMISSIONS.
       *
       * ⚠ **Why the LABEL and not the tile.** Hovering anywhere on the tile would put two
       * bubbles in the top layer at once — this one and the headline figure's own disclosure,
       * which is inside it — anchored ~34px apart and overlapping. The two are not the same
       * panel: the figure hands back that one number's digits, this hands back the number *and*
       * its parts, which are what the 11px subtext compacts. Two disjoint targets, one bubble
       * each; making the tile the surface means giving one of them up.
       *
       * The trigger inherits the label's type: `.disclosure` sets `font: inherit` and
       * `color: inherit`, and `text-transform` / `letter-spacing` inherit on their own, so the
       * row looks exactly as it did before — apart from `cursor: help`, which is the product's
       * standing signal that something is hiding here.
       */}
      <Disclosure
        /*
         * Named so it cannot be confused with the figure's own disclosure, which announces
         * `Active: 798`. Two triggers on one tile have to be told apart by ear as well as by
         * eye: this one is the figure and its parts, that one is the figure alone.
         */
        accessibleName={`${spoken ? `${label}, ${spoken}` : label} — the figures behind it`}
        panel={cardPanel(spoken ? `${label} · ${spoken}` : label, value, figures, provenance)}
        style={{
          width: '100%',
          display: 'flex',
          fontSize: fontSize.label,
          fontWeight: 600,
          letterSpacing: '.4px',
          color: color.inkSubtle,
          textTransform: 'uppercase',
        }}
      >
        <span>
          {label}
          {qualifier ? (
            <span style={{ color: color.inkFaint, fontWeight: 500 }}> · {qualifier}</span>
          ) : null}
        </span>
      </Disclosure>
      <div style={{ marginTop: 5 }}>
        <Figure
          label={spoken ? `${label}, ${spoken}` : label}
          metric={value}
          figures={figures}
          provenance={provenance}
          color={valueColor}
        />
      </div>
      {subtext ? (
        <div style={{ fontSize: fontSize.caption, color: color.inkSubtle, marginTop: 1 }}>
          {subtext}
        </div>
      ) : null}
      {children}
    </div>
  );
}

/**
 * The clinical split, as a proportion rather than two more numbers (owner request 2026-08-29).
 *
 * Three cards on this strip already print their Nursing/Allied split in 11px text — `14K Nursing
 * · 6,113 Allied`. The figures are the record; this is the reading. A 5px bar answers "roughly
 * two-thirds nursing" before the eye has parsed a digit, which is the question actually being
 * asked of a composition.
 *
 * ## Why a bar is safe HERE and would not be on every card
 *
 * It is a **composition of the same as-of figure**, so it introduces no period and no filter —
 * which is what keeps it inside FR-029. A sparkline would not: a trend implies the window this
 * strip deliberately ignores, and the card that "answers to nothing" would suddenly appear to
 * answer to the timeline.
 *
 * ## The rule for adding one
 *
 * The segments must be **complementary parts of one whole** — Nursing and Allied are, and each of
 * BoB, Jobs and Submissions sums to its own headline. They must never be *independent measures*:
 * `toaInternal + toaTarget ≠ activeToa` by design (see the specialties invariants), and a bar
 * across those two would draw a whole they do not form. The widths here are proportional to the
 * segments' own sum, so the bar states a ratio between its parts and asserts nothing about the
 * headline above it — but a reader will still read a full-width bar as "all of it", so the
 * complementary test is the one that matters, not the arithmetic.
 *
 * `aria-hidden`, deliberately. Both figures are already in the subtext as text, in the same DOM
 * order; announcing them again as a second graphic would make every card read twice.
 */
function CompositionBar({
  segments,
}: {
  segments: Array<{ value: number | null | undefined; color: string }>;
}) {
  const known = segments.filter(
    (s): s is { value: number; color: string } => typeof s.value === 'number' && s.value >= 0,
  );
  const total = known.reduce((sum, s) => sum + s.value, 0);
  // A partial split cannot be drawn honestly: one missing segment and the others would silently
  // expand to fill the whole (FR-055's rule, applied to a shape instead of a figure).
  if (known.length !== segments.length || total <= 0) return null;

  return (
    /*
     * Two elements, not one. `margin-top: auto` is what drops the bar to the foot of the tile so
     * every bar on the strip shares a baseline, and the gap above it therefore cannot be a margin
     * — but `box-sizing: border-box` is global here, so putting the gap on the 5px bar as padding
     * would leave it 0px of content and no visible segments at all.
     */
    <div aria-hidden="true" style={{ marginTop: 'auto', paddingTop: 8 }}>
      <div
        style={{
          display: 'flex',
          height: 5,
          borderRadius: 3,
          // The track shows through wherever rounding leaves a sliver, so it has to be a real
          // colour — on a tile, transparent reads as a gap in the bar rather than as its floor.
          background: color.surfaceTrack,
          overflow: 'hidden',
        }}
      >
        {known.map((segment) => (
          <div
            key={segment.color}
            style={{ width: `${(segment.value / total) * 100}%`, background: segment.color }}
          />
        ))}
      </div>
    </div>
  );
}

export function KpiStrip({ kpi, unattributedBooks, timeseries, loading }: KpiStripProps) {
  if (loading) {
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(148px, 1fr))',
          gap: 12,
        }}
      >
        {Array.from({ length: 6 }, (_unused, i) => (
          <div key={i} className="skeleton" style={{ height: 92 }} aria-hidden="true" />
        ))}
      </div>
    );
  }

  if (!kpi) return null;

  /**
   * The Submissions card's figures, from the chart when there is one and from `kpi_summary`
   * when there is not.
   *
   * One object either way, so the headline, the split, the Int/Ext pair and the composition bar
   * are always the same window as each other and as the label naming it. Built as a fallback
   * rather than a branch for that reason: two code paths here is how a windowed total ends up
   * over an all-time split.
   */
  const submissions: SubmissionsWindow = submissionsOverWindow(timeseries) ?? {
    total: kpi.submissions?.total?.value ?? null,
    nursing: kpi.submissions?.nursing?.value ?? null,
    allied: kpi.submissions?.allied?.value ?? null,
    internal: kpi.submissions?.internal?.value ?? null,
    external: kpi.submissions?.external?.value ?? null,
    // Two glyphs shorter than the shortest windowed face, so the fallback needs no abbreviation
    // and the two forms are one string.
    label: 'all time',
    spokenLabel: 'all time',
  };

  const nursingSpan = (n: number | null | undefined) => (
    <span style={{ color: color.nursing, fontWeight: 600 }}>{formatValue(n)}</span>
  );
  const alliedSpan = (n: number | null | undefined) => (
    <span style={{ color: color.allied, fontWeight: 600 }}>{formatValue(n)}</span>
  );
  /*
   * `color.target` — the hue the breakdown panel already gives a Target row, so "target" means
   * one colour wherever it is printed. Deliberately NOT `colTarget`, which is the Specialties
   * table's ACTV-T column accent.
   */
  const targetSpan = (n: number | null | undefined) => (
    <span style={{ color: color.target, fontWeight: 600 }}>{formatValue(n)}</span>
  );

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(148px, 1fr))',
        gap: 12,
      }}
      aria-label="Key figures"
    >
      <Card
        label="Clusters"
        metricKey="clusters"
        figures={[
          neutralRow('Regions', kpi.clusters?.regions),
          neutralRow('Special', kpi.clusters?.special),
        ]}
        value={kpi.clusters?.total ?? null}
        subtext={`${kpi.clusters?.regions ?? EM_DASH} Regions, ${
          kpi.clusters?.special ?? EM_DASH
        } Special`}
      />

      <Card
        label="Recruiters"
        metricKey="recruiters"
        figures={[neutralRow('Manager seats', kpi.managers?.value)]}
        value={kpi.recruiters?.value ?? null}
        // The in-scope recruiter **headcount**, equal to the sum of the pills and to the table's
        // REC totals row (FR-029a). Manager seats are the second figure, not part of the first:
        // the headline used to add them, which double-counted anyone who does both — the two sets
        // key on an email and on a name and cannot be de-duplicated (§9.7.2).
        subtext={`${formatValue(kpi.managers?.value ?? null)} manager seats`}
      />

      <Card
        label="Active"
        metricKey="active"
        /*
         * No rows: the card has no split, and the panel's `Total` is the whole of it. The
         * disclosure still earns its place — 552 prints exact but 1,204 prints `1.20K`, and
         * this is the only place the digits come back.
         */
        figures={[]}
        value={kpi.active?.value ?? null}
        // `seriesColor.toa`, not the literal `#1d4ed8` it was: the same hue, but as a token it
        // lightens for a dark surface. Active is TOA, so it borrows the chart's own blue —
        // exactly what the mirror in `tokens.ts` exists for.
        valueColor={seriesColor.toa}
        subtext="travelers on assignment"
      />

      <Card
        label="BoB"
        metricKey="bob"
        figures={breakdownRows({
          total: kpi.bob?.total?.value ?? null,
          nursing: kpi.bob?.nursing?.value ?? null,
          allied: kpi.bob?.allied?.value ?? null,
          includeSource: false,
          includeTargeting: false,
        })}
        value={kpi.bob?.total?.value ?? null}
        subtext={
          <>
            {nursingSpan(kpi.bob?.nursing?.value)} Nursing ·{' '}
            {alliedSpan(kpi.bob?.allied?.value)} Allied
          </>
        }
      >
        <CompositionBar
          segments={[
            { value: kpi.bob?.nursing?.value, color: color.nursing },
            { value: kpi.bob?.allied?.value, color: color.allied },
          ]}
        />
        <DataQualityNote unattributedBooks={unattributedBooks} figureLabel="BoB" />
      </Card>

      {/*
       * Open jobs, whole table. Responds to nothing (FR-029) — and the one card on this strip
       * that is NOT bounded to the UI regions: a job has no recruiter to fold (FR-029a).
       *
       * `N + A = the headline` since 2026-08-28: the card counts clinical open jobs, so Locums,
       * Non-clinical and IT are excluded from both. The server **sums the two halves it sends**
       * rather than serving a stored total, so the headline cannot disagree with the split
       * printed under it, and the excluded count never reaches the client (G-28, closed).
       *
       * ⚠ **`Tgt` is a THIRD reading of the same jobs, not a third slice** (owner request
       * 2026-08-30): of those open clinical jobs, how many are at a target account. It overlaps
       * both halves, so `N + A + Tgt` is not the headline and nothing here may sum it — which is
       * exactly why it stays out of the composition bar below. The bar draws complementary parts
       * of one whole and these three do not form one; the same reasoning that keeps
       * `toaInternal`/`toaTarget` out of one on Specialties.
       *
       * ⚠ **`Tgt` now carries its OWN split, and that one IS a partition** (owner request
       * 2026-09-02): `Tgt-N + Tgt-A = Tgt` exactly, because the target population is
       * clinical-only and sets nothing aside the way the headline sets aside Locums. Two
       * different relationships are on this one tile — an overlap between Tgt and the headline,
       * a partition inside Tgt — and the labels are what keep them apart. The split renders in
       * the target hue for the same reason: it belongs to `Tgt`, not to the `N`/`A` above it,
       * and colouring it with the offering hues would read as a fourth and fifth slice of the
       * headline.
       */}
      <Card
        label="Jobs"
        metricKey="kpiJobs"
        /*
         * `Target` keeps `breakdownRows`' own order — ahead of the two halves, as it is in the
         * table's panel and the chart's bubble — rather than the card face's `N · A · Tgt`. One
         * row grammar across the product is worth more than agreeing with the 11px line below,
         * and the target hue plus the label are what say which relationship each row is in:
         * `Total` is Nursing + Allied, `Target` is the same jobs counted a second way and is
         * summed into nothing, and its own two halves — appended here, since `breakdownRows` has
         * no axis for them — partition it exactly.
         */
        figures={[
          ...breakdownRows({
            total: kpi.jobs?.count ?? null,
            target: kpi.jobs?.target ?? null,
            nursing: kpi.jobs?.nursing ?? null,
            allied: kpi.jobs?.allied ?? null,
            includeSource: false,
          }),
          targetRow('Target · Nursing', kpi.jobs?.targetNursing),
          targetRow('Target · Allied', kpi.jobs?.targetAllied),
        ]}
        value={kpi.jobs?.count ?? null}
        subtext={
          /*
           * Non-breaking spaces between each figure and its own unit (2026-08-30).
           *
           * Three figures no longer fit one line in a 148px tile, and with ordinary spaces the
           * line broke wherever it ran out — orphaning `Tgt` under `111`, a label parted from the
           * number it names. The nbsp moves every break onto a `·`, which is where the reader
           * already sees a boundary. The separators stay ordinary spaces for exactly that reason.
           */
          <>
            {nursingSpan(kpi.jobs?.nursing)}&nbsp;N · {alliedSpan(kpi.jobs?.allied)}&nbsp;A ·{' '}
            {targetSpan(kpi.jobs?.target)}&nbsp;Tgt{' '}
            <span style={{ color: color.inkFaint }}>
              ({targetSpan(kpi.jobs?.targetNursing)}
              &nbsp;N ·&nbsp;
              {targetSpan(kpi.jobs?.targetAllied)}
              &nbsp;A)
            </span>
          </>
        }
      >
        <CompositionBar
          segments={[
            { value: kpi.jobs?.nursing, color: color.nursing },
            { value: kpi.jobs?.allied, color: color.allied },
          ]}
        />
      </Card>

      {/*
        ⚠ **The one card that answers to a control** (owner decision 2026-09-02).

        It was all-time and ignored everything (§9.7.6). It now totals the window the chart above
        it is drawing, and moves with every control that redraws that line — grain, Source,
        Targeting, Specialty, the region pills. The `qualifier` naming the window is part of the
        change, not a garnish: on a strip where nothing else moves, an unlabelled figure that
        changed when a chart control was touched would read as a fault.

        It falls back to the all-time figure when there is no chart payload — a snapshot with no
        series, or the first paint of a grain still in flight — which is why the whole card is
        expressed against `submissions` rather than being two cards behind a condition. The
        `subtext` figures and the composition bar come from the same object as the headline, so a
        fallback can never mix an all-time split under a windowed total.
      */}
      <Card
        label="Submissions"
        metricKey="kpiSubmissions"
        qualifier={submissions.label}
        qualifierSpoken={submissions.spokenLabel}
        /*
         * `breakdownRows` derives `External` as `total − internal` — the same rule
         * `submissionsOverWindow` applies to the card face, so the two cannot disagree — and
         * every row is read off the one `submissions` object, so a windowed total can never
         * sit over an all-time split.
         */
        figures={breakdownRows({
          total: submissions.total,
          internal: submissions.internal,
          nursing: submissions.nursing,
          allied: submissions.allied,
          includeTargeting: false,
        })}
        value={submissions.total}
        subtext={
          <>
            {nursingSpan(submissions.nursing)}N · {alliedSpan(submissions.allied)}A ·{' '}
            {formatValue(submissions.internal)}Int · {formatValue(submissions.external)}Ext
          </>
        }
      >
        {/*
          Nursing/Allied, not Internal/External — even though this card prints both splits.
          One bar per card, always the clinical one, so the teal-and-indigo bar means the same
          thing on every tile it appears on. Two bars here would make the strip's tallest card a
          different shape from its neighbours to show a second ratio nobody asked this card for.
        */}
        <CompositionBar
          segments={[
            { value: submissions.nursing, color: color.nursing },
            { value: submissions.allied, color: color.allied },
          ]}
        />
      </Card>
    </div>
  );
}
